import { CSSProperties, RefObject, useEffect, useRef, useState } from 'react'

export const MIN_SPLIT_VIEW_WIDTH_PERCENT = 30
export const MAX_SPLIT_VIEW_WIDTH_PERCENT = 60
const KEYBOARD_STEP_PERCENT = 2

const SPLIT_VIEW_WIDTH_VARIABLE = '--setting-split-view-width'
const SPLIT_VIEW_WIDTH_STORAGE_KEY = 'digitalTwinSettingsSplitViewWidth'
const COLLECTED_SETTINGS_SPLIT_VIEW_WIDTH_STORAGE_KEY = 'digitalTwinCollectedSettingsSplitViewWidth'
/** The width the <> button widens back to after narrowing the split view. */
const SPLIT_VIEW_RESTORE_WIDTH_STORAGE_KEY = 'digitalTwinSettingsSplitViewWidthRestore'
const COLLECTED_SETTINGS_SPLIT_VIEW_RESTORE_WIDTH_STORAGE_KEY = 'digitalTwinCollectedSettingsSplitViewWidthRestore'

/**
 * The room the main view leaves for the open panel on windows of 1366px and wider. Narrower windows
 * cannot fit both, so there the panel overlays the main view instead of squeezing it.
 */
export const SETTING_SPLIT_VIEW_SPACE_CLASS = 'min-[1366px]:mr-[var(--setting-split-view-width)]'
/** Shows that the panel lies over the main view, on windows where it overlays it. */
export const SETTING_SPLIT_VIEW_OVERLAY_SHADOW = { '@media (max-width: 1365.98px)': { boxShadow: 8 } }
export const SETTING_SPLIT_VIEW_WIDTH_CLASS = 'w-full lg:w-[var(--setting-split-view-width)]'

function clampWidthPercent(widthPercent: number): number {
  return Math.min(MAX_SPLIT_VIEW_WIDTH_PERCENT, Math.max(MIN_SPLIT_VIEW_WIDTH_PERCENT, widthPercent))
}

/** The width, in percent of the window, of a panel whose left edge is at `clientX` and right edge at `rightEdgeX`. */
export function getSplitViewWidthPercent(clientX: number, rightEdgeX: number, windowWidth: number): number {
  const widthPercent = ((rightEdgeX - clientX) / windowWidth) * 100
  return clampWidthPercent(Math.round(widthPercent * 10) / 10)
}

/** `currentTarget` is the resize handle, inside the panel whose right edge the width is measured from. */
type ResizeStartEvent = { preventDefault: () => void; currentTarget: Element }
type ResizeKeyEvent = { key: string; preventDefault: () => void }

function readStoredPercent(storageKey: string): number | null {
  try {
    const stored = Number(window.localStorage.getItem(storageKey) ?? Number.NaN)
    return Number.isFinite(stored) ? clampWidthPercent(stored) : null
  } catch {
    return null
  }
}

function storePercent(storageKey: string, percent: number | null): void {
  try {
    if (percent === null) {
      window.localStorage.removeItem(storageKey)
    } else {
      window.localStorage.setItem(storageKey, String(percent))
    }
  } catch {
    // Restricted browser storage: the width then lasts until the page is reloaded.
  }
}

export type SplitViewResizeHandlers = {
  widthPercent: number
  onResizeStart: (event: ResizeStartEvent) => void
  onResizeKeyDown: (event: ResizeKeyEvent) => void
  /** Whether `toggleNarrow` narrows the panel, rather than widening it again. */
  togglesToNarrow: boolean
  /**
   * Narrows the panel to its minimum width, or from there widens it again: back to the width it was
   * narrowed from, or to the maximum when there is none.
   */
  toggleNarrow: () => void
}

type SplitViewWidth = SplitViewResizeHandlers & {
  /** Holds the width both the panel and the space beside it read; put it around both. */
  containerRef: RefObject<HTMLDivElement | null>
  containerStyle: CSSProperties
}

/**
 * The split view's width, which the user drags between 30% and 60% of the window. It is remembered
 * once for the deviation and base settings views, and separately for collected settings. A drag
 * previews the width on the container and keeps it in state on release.
 */
export function useSplitViewWidth(isCollectedSettings: boolean): SplitViewWidth {
  const storageKey = isCollectedSettings
    ? COLLECTED_SETTINGS_SPLIT_VIEW_WIDTH_STORAGE_KEY
    : SPLIT_VIEW_WIDTH_STORAGE_KEY
  const restoreStorageKey = isCollectedSettings
    ? COLLECTED_SETTINGS_SPLIT_VIEW_RESTORE_WIDTH_STORAGE_KEY
    : SPLIT_VIEW_RESTORE_WIDTH_STORAGE_KEY
  const [widthPercent, setWidthPercentState] = useState(
    () => readStoredPercent(storageKey) ?? MIN_SPLIT_VIEW_WIDTH_PERCENT
  )
  const [restoreWidthPercent, setRestoreWidthPercentState] = useState(() => readStoredPercent(restoreStorageKey))

  const setRestoreWidthPercent = (percent: number | null): void => {
    setRestoreWidthPercentState(percent)
    storePercent(restoreStorageKey, percent)
  }
  const setWidthPercent = (percent: number): void => {
    setWidthPercentState(percent)
    storePercent(storageKey, percent)
  }
  /** A width the user drags or keys in replaces the one the <> button would have widened back to. */
  const resizeTo = (percent: number): void => {
    setWidthPercent(percent)
    setRestoreWidthPercent(null)
  }
  const togglesToNarrow = widthPercent > MIN_SPLIT_VIEW_WIDTH_PERCENT
  const toggleNarrow = (): void => {
    if (togglesToNarrow) {
      setRestoreWidthPercent(widthPercent)
      setWidthPercent(MIN_SPLIT_VIEW_WIDTH_PERCENT)
    } else {
      setWidthPercent(restoreWidthPercent ?? MAX_SPLIT_VIEW_WIDTH_PERCENT)
      setRestoreWidthPercent(null)
    }
  }
  const containerRef = useRef<HTMLDivElement>(null)
  const stopDraggingRef = useRef<(() => void) | null>(null)
  useEffect(() => () => stopDraggingRef.current?.(), [])

  const showWidth = (percent: number): void => {
    containerRef.current?.style.setProperty(SPLIT_VIEW_WIDTH_VARIABLE, `${percent}vw`)
  }

  const onResizeStart = (event: ResizeStartEvent): void => {
    event.preventDefault()
    const panelRightEdgeX = event.currentTarget.parentElement?.getBoundingClientRect().right ?? window.innerWidth
    let draggedPercent = widthPercent
    const previousUserSelect = document.body.style.userSelect
    document.body.style.userSelect = 'none'

    const handleMouseMove = (moveEvent: MouseEvent): void => {
      draggedPercent = getSplitViewWidthPercent(moveEvent.clientX, panelRightEdgeX, window.innerWidth)
      showWidth(draggedPercent)
    }
    const stopDragging = (): void => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.userSelect = previousUserSelect
      stopDraggingRef.current = null
    }
    const handleMouseUp = (): void => {
      stopDragging()
      resizeTo(draggedPercent)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    stopDraggingRef.current = stopDragging
  }

  const onResizeKeyDown = (event: ResizeKeyEvent): void => {
    const step = { ArrowLeft: KEYBOARD_STEP_PERCENT, ArrowRight: -KEYBOARD_STEP_PERCENT }[event.key]
    if (step === undefined) {
      return
    }
    event.preventDefault()
    resizeTo(clampWidthPercent(widthPercent + step))
  }

  return {
    widthPercent,
    onResizeStart,
    onResizeKeyDown,
    togglesToNarrow,
    toggleNarrow,
    containerRef,
    containerStyle: { [SPLIT_VIEW_WIDTH_VARIABLE]: `${widthPercent}vw` } as CSSProperties,
  }
}
